import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, Menu } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useAdmin } from "../context/AdminContext";
import { login as apiLogin, signup as apiSignup, searchCourses as apiSearchCourses } from "../services/api";
// import { searchCourses } from "../data/data";
import StudentLogin from "../pages/Studentlogin";

const Nav = ({ setShowStudentNavbar, showSearchBar = true }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname.toLowerCase();
    const { isLoggedIn, login, logout, user } = useAuth();
    const { adminLogin } = useAdmin();

    const [searchQuery, setSearchQuery] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredCourses, setFilteredCourses] = useState([]);

    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [activeAuthTab, setActiveAuthTab] = useState("login");
    const [loginForm, setLoginForm] = useState({ email: "", password: "" });
    const [signupForm, setSignupForm] = useState({
        name: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        role: "student",
    });
    const [authError, setAuthError] = useState("");
    const [showPassword, setShowPassword] = useState({
        login: false,
        signup: false,
        confirm: false,
    });

    // ⭐ Mobile Search Toggle
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);

    // ⭐ Click Outside Handler
    const mobileSearchRef = useRef(null);
    const desktopSearchRef = useRef(null);
    const profileDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                mobileSearchRef.current &&
                !mobileSearchRef.current.contains(event.target)
            ) {
                setShowMobileSearch(false);
            }
            if (
                desktopSearchRef.current &&
                !desktopSearchRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
            if (
                profileDropdownRef.current &&
                !profileDropdownRef.current.contains(event.target)
            ) {
                setShowProfileDropdown(false);
            }
        };

        if (showMobileSearch || showSuggestions || showProfileDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showMobileSearch, showSuggestions, showProfileDropdown]);

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const response = await apiLogin(loginForm);
            if (response.success) {
                login(response.user, response.token);
                
                // Redirect and update context based on the role returned by the backend
                if (response.user.role === 'admin') {
                    adminLogin();
                    setAuthModalOpen(false);
                    setAuthError("");
                    setLoginForm({ email: "", password: "" });
                    navigate('/admin/admindashboard');
                } else {
                    setAuthModalOpen(false);
                    setAuthError("");
                    setLoginForm({ email: "", password: "" });
                    navigate('/student/dashboard');
                }
            }
        } catch (error) {
            setAuthError(error.message || "Invalid email or password");
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        if (signupForm.password !== signupForm.confirmPassword) {
            setAuthError("Passwords do not match");
            return;
        }

        try {
            const response = await apiSignup({
                name: signupForm.name,
                email: signupForm.email,
                phone: signupForm.phone,
                password: signupForm.password,
                role: 'student' // Signup from main modal defaults to student
            });
            
            if (response.success) {
                if (response.token) {
                    login(response.user, response.token);
                    
                    // Redirect based on role (should be student)
                    setAuthModalOpen(false);
                    navigate('/student/dashboard');
                } else {
                    setActiveAuthTab("login");
                    setAuthError("Account created! Please sign in.");
                }
                
                setSignupForm({
                    name: "",
                    email: "",
                    password: "",
                    confirmPassword: "",
                    role: "student",
                });
            }
        } catch (error) {
            setAuthError(error.message || "Signup failed");
        }
    };

    const handleSearch = async (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (query.length > 0) {
            try {
                const response = await apiSearchCourses(query);
                if (response.success) {
                    setFilteredCourses(response.data.slice(0, 8));
                    setShowSuggestions(true);
                }
            } catch (error) {
                console.error("Search error:", error);
                // Fallback to local search if API fails (optional, but good for UX)
                // const results = searchCourses(query);
                // setFilteredCourses(results.slice(0, 8));
            }
        } else {
            setFilteredCourses([]);
            setShowSuggestions(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && filteredCourses.length > 0) {
            const firstCourse = filteredCourses[0];
            localStorage.setItem("selectedCourse", JSON.stringify(firstCourse));
            navigate("/courseoverview");
            setShowSuggestions(false);
            setShowMobileSearch(false);
        }
    };

    return (
        <>
            {/* NAVBAR */}
            <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md shadow-sm z-50 border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-14 sm:h-16">
                        {/* Logo */}
                        <a href="/" className="flex items-center gap-2">
                            <img src="/companylogo.png" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" alt="Logo" />
                            <h1 className="text-base sm:text-xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent truncate">
                                E-learning Hub
                            </h1>
                        </a>

                        {/* Desktop Search */}
                        {showSearchBar && (
                            <div ref={desktopSearchRef} className=" relative hidden md:block flex-1 max-w-xl mx-6">
                                <div className="relative">
                                    <Search
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                        size={20}
                                    />
                                    <input
                                        type="search"
                                        placeholder="Search course, topic..."
                                        value={searchQuery}
                                        onChange={handleSearch}
                                        onKeyDown={handleKeyDown}
                                        className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-full"
                                    />
                                </div>

                                {/* Desktop Suggestions */}
                                {showSuggestions && filteredCourses.length > 0 && (
                                    <div className="absolute bg-white border border-gray-300 rounded-lg shadow-xl w-full  mt-2 max-h-80 overflow-y-auto z-40">
                                        {filteredCourses.map((course) => (
                                            <div
                                                key={course.id}
                                                className="p-4 cursor-pointer hover:bg-emerald-50 border-b"
                                                onClick={() => {
                                                    localStorage.setItem(
                                                        "selectedCourse",
                                                        JSON.stringify(course)
                                                    );
                                                    navigate("/courseoverview");
                                                    setShowSuggestions(false);
                                                }}
                                            >
                                                <div className="font-semibold">{course.title}</div>
                                                <div className="text-sm text-gray-600">
                                                    {course.instructor} — {course.category}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="flex items-center gap-2">
                            {/* Mobile Search Icon */}
                            <button
                                className="md:hidden p-2 rounded-lg hover:bg-gray-100"
                                onClick={() => {
                                    if (!showMobileSearch) {
                                        setShowMobileSearch(true);
                                    } else {
                                        setShowSuggestions(false);
                                        setSearchQuery("");
                                    }
                                }}
                            >
                                <Search size={22} className="text-gray-700" />
                            </button>

                            {/* Menu / Login */}
                            {isLoggedIn ? (
                                <div className="flex items-center gap-4">
                                    {/* Mobile Hamburger menu */}
                                    <button
                                        onClick={() => {
                                            if (typeof setShowStudentNavbar === 'function') {
                                                setShowStudentNavbar(true);
                                            }
                                        }}
                                        className="p-2 rounded-lg hover:bg-gray-100 lg:hidden cursor-pointer"
                                    >
                                        <Menu size={24} className="text-gray-700" />
                                    </button>

                                    {/* Desktop Profile Dropdown */}
                                    <div ref={profileDropdownRef} className="relative hidden lg:block">
                                        <button
                                            onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                                            className="flex items-center gap-2 focus:outline-none"
                                        >
                                            {user?.image ? (
                                                <img
                                                    src={user.image}
                                                    alt="Profile"
                                                    className="w-9 h-9 rounded-full object-cover border-2 border-[#00b26f] hover:scale-105 transition-transform duration-300"
                                                />
                                            ) : (
                                                <div className="w-9 h-9 rounded-full bg-[#00b26f] hover:bg-[#008f5a] flex items-center justify-center text-white font-bold text-base border-2 border-white shadow-md hover:scale-105 transition-all duration-300">
                                                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                                </div>
                                            )}
                                        </button>

                                        {showProfileDropdown && (
                                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 animate-fadeIn">
                                                {/* User Info Header in Dropdown */}
                                                <div className="px-4 py-2 border-b border-gray-100 mb-1">
                                                    <p className="text-sm font-bold text-gray-900 truncate">{user?.name}</p>
                                                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                                                </div>

                                                <button
                                                    onClick={() => {
                                                        navigate('/');
                                                        setShowProfileDropdown(false);
                                                    }}
                                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                                                        currentPath === '/' || currentPath === '/home'
                                                            ? 'bg-emerald-50 text-emerald-700 font-semibold'
                                                            : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                                                    }`}
                                                >
                                                    <i className={`fas fa-home w-5 ${currentPath === '/' || currentPath === '/home' ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                                                    Home
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        navigate('/student/dashboard');
                                                        setShowProfileDropdown(false);
                                                    }}
                                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                                                        currentPath === '/student/dashboard'
                                                            ? 'bg-emerald-50 text-emerald-700 font-semibold'
                                                            : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                                                    }`}
                                                >
                                                    <i className={`fas fa-columns w-5 ${currentPath === '/student/dashboard' ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                                                    Dashboard
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        navigate('/student/MyCourses');
                                                        setShowProfileDropdown(false);
                                                    }}
                                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                                                        currentPath === '/student/mycourses'
                                                            ? 'bg-emerald-50 text-emerald-700 font-semibold'
                                                            : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                                                    }`}
                                                >
                                                    <i className={`fas fa-book w-5 ${currentPath === '/student/mycourses' ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                                                    My Courses
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        navigate('/student/certificates');
                                                        setShowProfileDropdown(false);
                                                    }}
                                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                                                        currentPath === '/student/certificates'
                                                            ? 'bg-emerald-50 text-emerald-700 font-semibold'
                                                            : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                                                    }`}
                                                >
                                                    <i className={`fas fa-certificate w-5 ${currentPath === '/student/certificates' ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                                                    Certificates
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        navigate('/student/profile');
                                                        setShowProfileDropdown(false);
                                                    }}
                                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                                                        currentPath === '/student/profile'
                                                            ? 'bg-emerald-50 text-emerald-700 font-semibold'
                                                            : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                                                    }`}
                                                >
                                                    <i className={`fas fa-user w-5 ${currentPath === '/student/profile' ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                                                    Profile
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        navigate('/student/support');
                                                        setShowProfileDropdown(false);
                                                    }}
                                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                                                        currentPath === '/student/support'
                                                            ? 'bg-emerald-50 text-emerald-700 font-semibold'
                                                            : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                                                    }`}
                                                >
                                                    <i className={`fas fa-headset w-5 ${currentPath === '/student/support' ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                                                    Support
                                                </button>

                                                <div className="border-t border-gray-100 mt-1 pt-1">
                                                    <button
                                                        onClick={async () => {
                                                            setShowProfileDropdown(false);
                                                            try {
                                                                const { logout: logoutApi } = require('../services/api');
                                                                await logoutApi();
                                                            } catch (error) {
                                                                console.error("Logout API failed:", error);
                                                            }
                                                            logout();
                                                            navigate('/');
                                                        }}
                                                        className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 font-medium"
                                                    >
                                                        <i className="fas fa-sign-out-alt w-5 text-red-400"></i>
                                                        Logout
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setAuthModalOpen(true)}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-1.5 sm:px-5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold shadow-sm hover:shadow transition-all duration-200"
                                >
                                    Login
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* ⭐ MOBILE SEARCH BAR (FULLSCREEN + FIXED + WORKING SUGGESTIONS) */}
            {/* MOBILE SEARCH BAR + SUGGESTIONS */}
            {showMobileSearch && (
                <div
                    ref={mobileSearchRef}
                    className="fixed top-16 left-0 right-0 bg-white px-4 pt-3 pb-4 shadow-lg md:hidden z-[999]"
                >
                    <div className="relative w-full">

                        {/* SEARCH INPUT */}
                        <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={20}
                        />
                        <input
                            type="search"
                            placeholder="Search course, topic..."
                            value={searchQuery}
                            onChange={handleSearch}
                            onKeyDown={handleKeyDown}
                            className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-full"
                        />

                        {/* ⭐ MOBILE SUGGESTIONS — FIXED CLICK ISSUE */}
                        {showSuggestions && filteredCourses.length > 0 && (
                            <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-300 rounded-xl shadow-2xl max-h-80 overflow-y-auto z-[9999]">
                                {filteredCourses.map((course) => (
                                    <div
                                        key={course.id}
                                        className="p-4 border-b hover:bg-emerald-50 cursor-pointer"
                                        onClick={() => {
                                            localStorage.setItem(
                                                "selectedCourse",
                                                JSON.stringify(course)
                                            );
                                            navigate("/courseoverview");
                                            setShowMobileSearch(false);
                                        }}
                                    >
                                        <div className="font-semibold">{course.title}</div>
                                        <div className="text-sm text-gray-600">
                                            {course.instructor} — {course.category}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>
                </div>
            )}

            {/* MODAL */}
            <StudentLogin
                authModalOpen={authModalOpen}
                setAuthModalOpen={setAuthModalOpen}
                activeAuthTab={activeAuthTab}
                setActiveAuthTab={setActiveAuthTab}
                loginForm={loginForm}
                setLoginForm={setLoginForm}
                signupForm={signupForm}
                setSignupForm={setSignupForm}
                authError={authError}
                setAuthError={setAuthError}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                handleLogin={handleLogin}
                handleSignup={handleSignup}
            />
        </>
    );
};

export default Nav;
